// M2 politics (M2_SPEC §9-§10, DESIGN §12.3-12.7): region closures, scripted region events (sabotage, attack,
// occupation, threat, expropriate), the annual expropriation hazard, rebel attacks, kidnapping, labor strikes,
// local partners, bribes, political-risk insurance, and the compensation that trickles in a year after a seizure.
// Inert before 1901: no RNG draws, no money moves.
import { allWells } from '../indexes.js'; // perf: per-day well list (same order as Object.values)
import { chance, range, int, nextFloat } from '../../core/rng.js';
import { dayToDate } from '../../core/time.js';
import { REGION_CLOSURES, regionClosure, partnersAvailable, LOCAL_PARTNERS, scheduledRoyalty } from '../../data/m2/regions.js';
import { dailyFromAnnual, POLITICAL_RISK_INSURANCE, severanceRate } from '../../data/m2/rules.js';
import { SCRIPTED } from '../../data/news.js';
// M3 econ: post-1946 seizures/closures, M3 local partners (data/m3/regions.js; not merged into the M2 tables)
import { regionClosureM3, NATIONALIZATIONS, M3_LOCAL_PARTNERS, nationalizationsAt } from '../../data/m3/regions.js';
const M3_YEAR = 1946;
const usCo = (state) => (state.company?.domicile ?? 'us') === 'us';
const hasCounsel = (state) => Object.values(state.crew ?? {}).some((c) => c.role === 'counsel' && c.status === 'active');
import { spotPrice, localPriceMult } from '../../economy/market.js';
import { eqDef, fieldDef, regionDef, regionRisk, unitPrice, eraDollars, yf, yearOf } from '../defs.js';
import { activeRigCrew, injureCrew, removeFromAssignments, unassignGuard, crewWage, roleName } from '../crew.js';
import { cancelTimer } from '../timers.js';
import { netback } from '../production.js';
import { sites, queueDecision, popDecision, startIncident } from '../security.js';
import { clamp, notify, log, spend, earn, nextId, fmt$ } from '../util.js';
import { m4RegionBlock } from '../m4/worldHooks.js'; // M4-C

// ---- tuning ---------------------------------------------------------------------------------
// Region `expropriation` keyframes are a risk *indicator* (0.2 in 1920s Mexico, 0.99 on the day). Rolled at face value
// every Mexican concession would be gone long before 1938, so the random hazard is scaled; the famous seizures are
// scripted (news + REGION_CLOSURES) and the random roll is suspended within a year of one.
export const EXPROP_HAZARD_MULT = 0.08;
export const EXPROP_HAZARD_CAP = 0.08;          // max effective annual chance from the random roll
export const SCHEDULED_WINDOW_DAYS = 400;       // no random seizure this close before a scripted one
export const DEFAULT_COMP_MAX = 0.3;            // compensation 0-30% of book value, less in corrupt regions
export const REBEL_SECURITY_DROP = { delta: -0.1, days: 90 };
export const BRIBE_NEEDED_PER_SITE = 60;        // 1901 $ / month per site for full effect
export const BRIBE_SCANDAL = { corruptionAbove: 0.6, perMonth: 0.02, rep: -5 };
export const KIDNAP = { ransomBase: 1000, baseYear: 1915, negotiateDays: [20, 60], releasePerWeek: 0.15, deathPerWeek: 0.05, ignoreRep: -10, ignoreDeath: 0.5 };
export const LABOR_STRIKE = { days: 30, wageRaise: 0.2 };
export const INSURER_RAW_WEIGHT = 0.25;         // underwriters price off the headline risk too
export const INSURANCE_MIN_RATE = 0.005;
// Extra hazards by period (region `hazards` lists are era-agnostic): the Mexican oil workers' union strikes of 1936-37.
// time-limited regional hazards. Piracy (offshore sites only — security.js): Gulf of Guinea from the 1990s
// (the Niger Delta militancy and boardings of rigs, FPSOs and supply boats), Lake Maracaibo's 'piratas del lago'
// from 2008, and the Bay of Campeche platform raids from 2015.
export const EXTRA_HAZARDS = {
  mexico: [{ hazard: 'strike', from: 1936.0, until: 1938.21 }, { hazard: 'piracy', from: 2015, until: 9999 }],
  nigeria: [{ hazard: 'piracy', from: 1999, until: 9999 }],
  venezuela: [{ hazard: 'piracy', from: 2008, until: 9999 }],
};
export const M2_FROM = 1901;

export const m2On = (state) => yf(state) >= M2_FROM;

export function regionState(state, rid) {
  state.regions ??= {};
  const r = (state.regions[rid] ??= { securityMod: 0, incidents: [], partner: null, bribe: 0, insurance: null });
  r.incidents ??= [];
  return r;
}

export function politicsState(state) {
  state.politics ??= { kidnappings: {}, compensations: [], policies: {}, seizures: [] };
  return state.politics;
}

export const regionOfField = (fieldId) => fieldDef(fieldId)?.regionId ?? null;
export function fieldsInRegion(state, rid) {
  return Object.keys(state.fields).filter((fid) => (state.fields[fid].regionId ?? regionOfField(fid)) === rid);
}

export function extraHazards(rid, y) {
  return (EXTRA_HAZARDS[rid] ?? []).filter((h) => y >= h.from && y < h.until).map((h) => h.hazard);
}

// ---- access rules (closures, occupation) ------------------------------------------------------
export function closureNow(state, rid) {
  if (!rid) return null;
  const y = yf(state);
  if (y >= M3_YEAR) { // M3 econ: + M3 'seize' records (by date, or already applied by their news item)
    const c = regionClosureM3(rid, y, REGION_CLOSURES, { usCompany: usCo(state) });
    if (c) return c;
    const ap = state.m3econ?.nat?.applied;
    const n = ap && NATIONALIZATIONS.find((x) => x.regionId === rid && x.mode === 'seize' && ap[x.id] != null && y < x.until && (x.appliesTo !== 'us' || usCo(state)));
    return n ? { from: n.from, until: n.until, compensation: n.compensation, reason: n.reason, id: n.id } : null;
  }
  return regionClosure(rid, y);
}

// Reason string when the player may not lease / spud / build in this field's region, else null.
export function regionBlock(state, fieldId) {
  if (!m2On(state)) return null;
  const rid = regionOfField(fieldId);
  if (!rid) return null;
  const c = closureNow(state, rid);
  if (c) return `${regionDef(rid)?.name ?? rid} is closed to foreign operators (${c.reason}).`;
  const reg = state.regions?.[rid];
  if (reg?.occupiedUntil > state.day) return `${regionDef(rid)?.name ?? rid} is under military occupation; no new leases or spuds until ${dayStr(reg.occupiedUntil)}.`;
  return m4RegionBlock(state, fieldId); // M4-C: sanctions' new-investment bans (null before 2000)
}
const dayStr = (d) => { const { year, month, dom } = dayToDate(d); return `${year}-${String(month).padStart(2, '0')}-${String(dom).padStart(2, '0')}`; };

// Security adjustments for security.effectiveRisk: temporary drops (rebel attacks) and the occupation cap.
export function adjustSecurity(state, rid, security) {
  const reg = state.regions?.[rid];
  if (!reg) return security;
  let s = security;
  for (const m of reg.tempMods ?? []) if (m.untilDay > state.day) s += m.delta;
  if (reg.occupiedUntil > state.day) s = Math.min(s, 0.1);
  return clamp(s, 0, 1);
}

// ---- partners & bribes ------------------------------------------------------------------------
export function partnerDef(rid, pid) { return [...(LOCAL_PARTNERS[rid] ?? []), ...(M3_LOCAL_PARTNERS[rid] ?? [])].find((p) => p.id === pid) ?? null; }
// M3 econ: partners on offer = M2 table + the M3 additions (from 1946)
export function partnersOnOffer(rid, y) {
  const m2 = partnersAvailable(rid, y);
  return y >= M3_YEAR ? [...m2, ...(M3_LOCAL_PARTNERS[rid] ?? []).filter((p) => y >= p.from && y < p.until)] : m2;
}
export function activePartner(state, rid) {
  const pid = state.regions?.[rid]?.partner;
  if (!pid) return null;
  const p = partnerDef(rid, pid);
  return p && yf(state) >= p.from && yf(state) < p.until ? p : null;
}

export function bribeNeeded(state, rid) {
  const n = Math.max(1, sites(state).filter((w) => regionOfField(w.fieldId) === rid).length);
  const corr = regionRisk(rid, yf(state)).corruption;
  return eraDollars(state, BRIBE_NEEDED_PER_SITE, 1901) * n * (0.5 + corr);
}

export function bribeEffect(state, rid) {
  const reg = state.regions?.[rid];
  if (!(reg?.bribe > 0)) return 0;
  const corr = regionRisk(rid, yf(state)).corruption;
  return 0.5 * corr * Math.min(1, reg.bribe / bribeNeeded(state, rid));
}

// Multiplier on the per-site attack chance (security.attackChance).
export function attackMult(state, rid) {
  if (!m2On(state)) return 1;
  const p = activePartner(state, rid);
  return (p ? p.attackMult : 1) * (1 - bribeEffect(state, rid));
}

export function setPartnerImpl(state, rid, pid) {
  if (!regionDef(rid)) return { ok: false, msg: 'No such region.' };
  const reg = regionState(state, rid);
  if (pid == null) {
    if (!reg.partner) return { ok: false, msg: 'You have no local partner there.' };
    const old = partnerDef(rid, reg.partner);
    reg.partner = null;
    return { ok: true, msg: `You part ways with ${old?.name ?? 'your partner'}.` };
  }
  const p = partnersOnOffer(rid, yf(state)).find((x) => x.id === pid);
  if (!p) return { ok: false, msg: 'That partner is not on offer now.' };
  reg.partner = p.id;
  return { ok: true, msg: `${p.name}: ${Math.round(p.revenueShare * 100)}% of revenue in ${regionDef(rid).name}. Attack risk ×${p.attackMult}.` };
}

export function payBribeImpl(state, rid, perMonth) {
  if (!regionDef(rid)) return { ok: false, msg: 'No such region.' };
  const amt = Math.max(0, Math.round(Number(perMonth) || 0));
  const reg = regionState(state, rid);
  reg.bribe = amt;
  if (!amt) return { ok: true, msg: 'Envelopes stopped.' };
  const need = bribeNeeded(state, rid);
  return { ok: true, msg: `${fmt$(amt)} a month to the right people in ${regionDef(rid).name} (full effect at ${fmt$(need)}).` };
}

// Partner share of each sale (called with severance from production.shipAndSell via m2/index.onSale).
export function chargePartnerShare(state, fieldId, gross) {
  if (!(gross > 0) || !m2On(state)) return 0;
  const p = activePartner(state, regionOfField(fieldId));
  if (!p) return 0;
  const amt = gross * p.revenueShare;
  spend(state, amt, 'protection', false);
  return amt;
}

// ---- scheduled seizures (for the hazard window and the UI) ----------------------------------------
let SCHEDULE = null;
export function scheduledSeizures() {
  if (SCHEDULE) return SCHEDULE;
  const list = [];
  for (const n of SCRIPTED) if (n.effect?.type === 'regionEvent' && n.effect.event === 'expropriate') list.push({ regionId: n.effect.regionId, day: n.day, id: n.id, compensation: n.effect.compensation ?? 0 });
  for (const [rid, cs] of Object.entries(REGION_CLOSURES)) for (const c of cs) list.push({ regionId: rid, year: c.from, id: `closure_${rid}_${c.from}`, compensation: c.compensation });
  for (const n of NATIONALIZATIONS) list.push({ regionId: n.regionId, year: n.from, id: n.id, compensation: n.compensation ?? 0, m3: true }); // M3 econ
  SCHEDULE = list;
  return list;
}
function nextScheduled(state, rid) {
  const y = yf(state);
  let best = null;
  for (const s of scheduledSeizures()) {
    if (s.regionId !== rid) continue;
    const dt = s.day != null ? s.day - state.day : (s.year - y) * 365.25;
    if (dt >= 0 && (!best || dt < best.inDays)) best = { ...s, inDays: dt };
  }
  return best;
}

export function expropriationHazard(state, rid) {
  const raw = regionRisk(rid, yf(state)).expropriation ?? 0;
  const p = activePartner(state, rid);
  const sched = nextScheduled(state, rid);
  // M3 econ: no random seizure where the state has already taken a share, a fee contract or everything
  const suspended = (!!sched && sched.inDays <= SCHEDULED_WINDOW_DAYS) || (yf(state) >= M3_YEAR && nationalizationsAt(rid, yf(state), { usCompany: usCo(state) }).length > 0);
  const threat = state.regions?.[rid]?.threatUntil > state.day ? 1.5 : 1;
  const annual = suspended || closureNow(state, rid) ? 0 : Math.min(EXPROP_HAZARD_CAP, raw * EXPROP_HAZARD_MULT * threat) * (p?.expropriationMult ?? 1);
  return { annualRaw: raw, annual, daily: annual > 0 ? dailyFromAnnual(annual) : 0, scheduled: sched && sched.inDays < 3 * 365 ? { inDays: Math.round(sched.inDays) } : null, suspended };
}

// ---- assets & book value ---------------------------------------------------------------------
const GONE = new Set(['SEIZED', 'PLUGGED', 'HANDED_OVER']);
function regionAssets(state, rid) {
  const fids = new Set(fieldsInRegion(state, rid));
  const wells = Object.values(state.wells).filter((w) => fids.has(w.fieldId) && !GONE.has(w.status) && !w.ownerOperatorId);
  const rigs = Object.values(state.rigs).filter((r) => (r.at && fids.has(r.at.fieldId)) || (r.wellId && fids.has(state.wells[r.wellId]?.fieldId)));
  const rigIds = new Set(rigs.map((r) => r.uid));
  const wellIds = new Set(Object.values(state.wells).filter((w) => fids.has(w.fieldId)).map((w) => w.id));
  const items = Object.values(state.items).filter((i) => (i.loc.type === 'field' && fids.has(i.loc.id)) || (i.loc.type === 'rig' && rigIds.has(i.loc.id))
    || ((i.loc.type === 'well' || i.loc.type === 'site') && (wellIds.has(i.loc.id) || rigIds.has(i.loc.id) || fids.has(i.loc.id))));
  const plots = [];
  for (const fid of fids) state.fields[fid].plots.forEach((p, idx) => { if (p.owned) plots.push({ fid, idx, p }); });
  const oil = [...fids].reduce((a, fid) => a + (state.fields[fid].storage?.bbl ?? 0), 0);
  const refineries = Object.values(state.refineries ?? {}).filter((r) => r.siteId === 'field_side' && fids.has(r.fieldId));
  return { fids, wells, rigs, items, plots, oil, refineries };
}

export function hasAssets(state, rid) {
  const a = regionAssets(state, rid);
  return a.wells.length + a.rigs.length + a.items.length + a.plots.length + a.refineries.length > 0 || a.oil > 1;
}

export function bookValue(state, rid, a = regionAssets(state, rid)) {
  const spot = Math.max(0, spotPrice(state));
  let v = 0;
  for (const r of a.rigs) v += unitPrice(state, eqDef(r.defId)) * 0.5 * r.condition / 100;
  for (const i of a.items) { const d = eqDef(i.defId); if (d) v += unitPrice(state, d) * 0.5 * (i.condition ?? 100) / 100; }
  for (const { p } of a.plots) if (!p.wellId) v += (p.leasePaid ?? 0) * 0.7;
  v += a.oil * spot * 0.7;
  // producing wells: ~a year of net cash flow at today's netback (what a buyer would pay for the lease)
  const nbBy = {};
  for (const w of a.wells) {
    if (!w.prod || !['PRODUCING', 'SHUT_IN'].includes(w.status)) continue;
    const nb = (nbBy[w.fieldId] ??= Math.max(0.05 * spot, netback(state, w.fieldId).net));
    v += (w.prod.rate || w.prod.potential || 0) * 365 * nb * 0.6;
  }
  for (const ref of a.refineries) v += (ref.units?.length ?? 1) * eraDollars(state, 2000, 1865);
  return v;
}

// ---- seizure ----------------------------------------------------------------------------------
export function seizeRegion(state, rid, compFrac = null, reason = 'Expropriation', opts = {}) {
  const a = regionAssets(state, rid);
  const reg = regionState(state, rid);
  const any = a.wells.length + a.rigs.length + a.items.length + a.plots.length + a.refineries.length > 0 || a.oil > 1;
  if (!any) return null;
  const value = bookValue(state, rid, a);
  const corr = regionRisk(rid, yf(state)).corruption;
  let frac = compFrac ?? Math.round(DEFAULT_COMP_MAX * (1 - corr) * nextFloat(state) * 100) / 100;
  if (yf(state) >= M3_YEAR && hasCounsel(state)) frac = Math.round(frac * 1.2 * 100) / 100; // M3 econ: counsel +20%
  const fids = a.fids;
  const seizedWellIds = new Set();
  const rigIds = new Set(a.rigs.map((r) => r.uid));
  // wells
  for (const w of allWells(state)) {
    if (!fids.has(w.fieldId) || GONE.has(w.status)) continue;
    seizedWellIds.add(w.id);
    for (const t of Object.values(state.timers)) if (t.refId === w.id) cancelTimer(state, t.id);
    w.status = 'SEIZED';
    w.seized = { day: state.day, regionId: rid };
    w.timerId = null;
    w.problem = null;
    if (w.prod) { w.prod.rate = 0; w.prod.hot = 0; }
    w.gusher = w.gusher ? { ...w.gusher, rate: 0 } : w.gusher;
    if (w.rigId) rigIds.add(w.rigId);
    w.rigId = null;
    const plot = state.fields[w.fieldId].plots[w.plot];
    if (plot) { plot.owned = false; plot.seized = true; plot.leaseExpires = null; }
  }
  // rigs and what is on them; crews come home
  for (const uid of rigIds) {
    const r = state.rigs[uid];
    if (!r) continue;
    for (const cid of [...(r.crewIds ?? [])]) { const c = state.crew[cid]; if (c) removeFromAssignments(state, c); }
    for (const t of Object.values(state.timers)) if (t.refId === uid) cancelTimer(state, t.id);
    for (const i of Object.values(state.items)) if (i.loc.type === 'rig' && i.loc.id === uid) delete state.items[i.uid];
    delete state.rigs[uid];
  }
  // field items (tanks, lift plants), site forts, well items
  for (const i of Object.values(state.items)) {
    const l = i.loc;
    if ((l.type === 'field' && fids.has(l.id)) || ((l.type === 'well' || l.type === 'site') && (seizedWellIds.has(l.id) || rigIds.has(l.id) || fids.has(l.id)))) delete state.items[i.uid];
  }
  // leases, storage, guards
  for (const fid of fids) {
    const f = state.fields[fid];
    for (const p of f.plots) if (p.owned) { p.owned = false; p.seized = true; p.leaseExpires = null; p.leaseDay = null; }
    f.storage.bbl = 0; f.storage.barrels = 0; f.storage.capacityBbl = 0; f.shipQueue = 0;
    if (f.shipTimerId) { cancelTimer(state, f.shipTimerId); f.shipTimerId = null; }
    if (f.hot) { f.hot.mult = 0; f.hot.log = []; f.hot.monthBbl = 0; }
    if (state.refining?.routes?.[fid]) delete state.refining.routes[fid];
  }
  for (const c of Object.values(state.crew)) {
    if (c.siteId && (fids.has(c.siteId) || seizedWellIds.has(c.siteId) || rigIds.has(c.siteId))) unassignGuard(state, c);
  }
  for (const inc of Object.values(state.incidents ?? {})) {
    if (inc.status === 'pending' && seizedWellIds.has(inc.wellId)) { inc.status = 'resolved'; inc.outcome = 'seized'; popDecision(state, inc.id); }
  }
  for (const ref of a.refineries) {
    delete state.refineries[ref.uid];
    for (const [fid, uid] of Object.entries(state.refining?.routes ?? {})) if (uid === ref.uid) delete state.refining.routes[fid];
  }
  // compensation (a year later) and insurance (now)
  const P = politicsState(state);
  const compensation = value * frac;
  if (compensation > 0.5) P.compensations.push({ regionId: rid, amount: Math.round(compensation * 100) / 100, dueDay: state.day + (opts.compensationDays ?? 365), reason });
  const insured = insurancePayout(state, rid, value);
  if (insured > 0) earn(state, insured, 'recoveries');
  P.seizures.push({ day: state.day, regionId: rid, value: Math.round(value), compensation: Math.round(compensation), compFrac: frac, insured: Math.round(insured), reason });
  reg.seized = { day: state.day, reason };
  reg.partner = null; reg.bribe = 0; reg.strike = null;
  state.company.stats.expropriations = (state.company.stats.expropriations ?? 0) + 1;
  const name = regionDef(rid)?.name ?? rid;
  notify(state, 'expropriation', {
    regionId: rid, value, compensation, compFrac: frac, insured, pause: true, level: 'major',
    text: `${reason}: everything you held in ${name} now belongs to the state (book value ${fmt$(value)}).${compensation > 0.5 ? ` Compensation of ${fmt$(compensation)} promised within a year.` : ' No compensation.'}${insured > 0 ? ` Insurers pay ${fmt$(insured)}.` : ''}`,
  });
  return { value, compensation, insured, frac };
}

// ---- political-risk insurance (Lloyd's, from 1919) --------------------------------------------
export function insuranceRate(state, rid) {
  const h = expropriationHazard(state, rid);
  const raw = regionRisk(rid, yf(state)).expropriation ?? 0;
  const threat = state.regions?.[rid]?.threatUntil > state.day ? 2 : 1;
  const rate = Math.max(INSURANCE_MIN_RATE, raw * INSURER_RAW_WEIGHT, h.annual) * POLITICAL_RISK_INSURANCE.premiumMult * threat;
  // M3 econ: from 1971 US companies can buy OPIC cover (Overseas Private Investment Corporation): priced on the
  // actual hazard, not the headline risk, with a 1.5%/yr floor
  if (yf(state) >= OPIC_FROM && usCo(state)) return Math.min(rate, Math.max(OPIC_MIN_RATE, h.annual * POLITICAL_RISK_INSURANCE.premiumMult) * threat);
  return rate;
}
export const OPIC_FROM = 1971.0;
export const OPIC_MIN_RATE = 0.015;

export function insuranceQuote(state, rid, insuredValue = null) {
  const value = insuredValue ?? Math.round(bookValue(state, rid) * 1.25);
  const y = yf(state);
  let reason = null;
  if (y < POLITICAL_RISK_INSURANCE.fromYear) reason = `Lloyd’s writes political-risk cover from ${POLITICAL_RISK_INSURANCE.fromYear}.`;
  else if (closureNow(state, rid)) reason = 'Nothing left to insure: the region is closed.';
  else if ((regionRisk(rid, y).expropriation ?? 0) <= 0) reason = 'No political risk to insure here.';
  else if (!(value > 0)) reason = 'Nothing to insure yet.';
  const rate = insuranceRate(state, rid);
  return { available: !reason, reason, insuredValue: value, rate, premiumPerYear: value * rate, payout: POLITICAL_RISK_INSURANCE.payout, maxYears: POLITICAL_RISK_INSURANCE.maxCoverYears };
}

export function buyInsuranceImpl(state, rid, { insuredValue = null, years = 1 } = {}) {
  if (!regionDef(rid)) return { ok: false, msg: 'No such region.' };
  const q = insuranceQuote(state, rid, insuredValue);
  if (!q.available) return { ok: false, msg: q.reason };
  const yrs = clamp(Math.round(years), 1, POLITICAL_RISK_INSURANCE.maxCoverYears);
  if (!spend(state, q.premiumPerYear, 'insurance')) return { ok: false, msg: `The first year’s premium is ${fmt$(q.premiumPerYear)}.` };
  const P = politicsState(state);
  P.policies[rid] = { insuredValue: q.insuredValue, premiumPerYear: q.premiumPerYear, startDay: state.day, untilDay: state.day + 365 * yrs, paidUntilDay: state.day + 365 };
  regionState(state, rid).insurance = { untilDay: state.day + 365 * yrs, insuredValue: q.insuredValue };
  return { ok: true, msg: `Political-risk cover on ${fmt$(q.insuredValue)} in ${regionDef(rid).name} for ${yrs} year${yrs > 1 ? 's' : ''}: ${fmt$(q.premiumPerYear)} a year, pays ${Math.round(q.payout * 100)}%.` };
}

export function cancelInsuranceImpl(state, rid) {
  const P = politicsState(state);
  if (!P.policies[rid]) return { ok: false, msg: 'No policy there.' };
  delete P.policies[rid];
  if (state.regions?.[rid]) state.regions[rid].insurance = null;
  return { ok: true, msg: 'Policy cancelled (no refund).' };
}

function insurancePayout(state, rid, value) {
  const P = politicsState(state);
  const pol = P.policies[rid];
  let pay = 0;
  if (pol && state.day < pol.untilDay && state.day < pol.paidUntilDay) pay = POLITICAL_RISK_INSURANCE.payout * Math.min(value, pol.insuredValue);
  const lloyds = state.tokens?.insurance?.[rid];
  if (lloyds?.kind === 'lloyds' && (lloyds.untilDay ?? 0) > state.day) pay = Math.max(pay, value); // token policy: 100%
  if (pol) { delete P.policies[rid]; if (state.regions?.[rid]) state.regions[rid].insurance = null; }
  return pay;
}

// ---- incidents: rebel attack, kidnapping, labor strike ------------------------------------------
// security.applyIncidentEffect → case 'rebel_attack'
export function applyRebelAttack(state, inc, well, exposedCrew) {
  const f = state.fields[well.fieldId];
  const rig = well.rigId ? state.rigs[well.rigId] : null;
  let txt = '';
  if (rig) {
    const dmg = range(state, 40, 90);
    rig.condition = Math.max(0, rig.condition - dmg);
    txt += `Rebels wrecked ${rig.name} (−${Math.round(dmg)}%). `;
  }
  if (chance(state, 0.3)) {
    txt += 'They fired the derrick! ';
    well.fire = { day: state.day, untilDay: state.day + 5 };
    if (well.attacked) well.attacked.fire = true;
    if (rig) rig.condition = Math.max(0, rig.condition - 20);
  }
  if (well.prod) {
    const days = int(state, 20, 60);
    well.prod.forcedUntil = Math.max(well.prod.forcedUntil ?? 0, state.day + days);
    txt += `${well.name} is down for ${days} days. `;
  }
  if (exposedCrew.length && chance(state, 0.4)) {
    const c = exposedCrew[Math.floor(nextFloat(state) * exposedCrew.length)];
    injureCrew(state, c, 'was shot in the rebel attack', { fatalChance: 0.1, cause: 'rebel attack', wellId: well.id });
  }
  if (f.storage.bbl > 1) {
    const pct = range(state, 0.3, 0.8);
    const lost = f.storage.bbl * pct;
    f.storage.bbl -= lost;
    f.storage.barrels = Math.max(0, Math.floor(f.storage.barrels * (1 - pct)));
    txt += `${Math.round(lost).toLocaleString('en-US')} bbl requisitioned or burned.`;
  }
  const reg = regionState(state, inc.regionId);
  (reg.tempMods ??= []).push({ delta: REBEL_SECURITY_DROP.delta, untilDay: state.day + REBEL_SECURITY_DROP.days, why: 'rebel_attack' });
  return txt.trim() || 'The rebels rode through and left.';
}

// security.startIncident delegates these two types here. Return the record, or null if the type does not apply.
export function startKidnapping(state, well) {
  const rid = regionOfField(well.fieldId);
  const rig = well.rigId ? state.rigs[well.rigId] : null;
  let pool = rig ? activeRigCrew(state, rig) : [];
  if (!pool.length) pool = Object.values(state.crew).filter((c) => c.status === 'active' && !c.injuredUntil && c.rigId && regionOfField(state.rigs[c.rigId]?.at?.fieldId) === rid);
  if (!pool.length) return null;
  const c = pool[Math.floor(nextFloat(state) * pool.length)];
  const P = politicsState(state);
  const id = nextId(state, 'k');
  const vis = 1 + 0.5 * ((well.prod?.rate ?? 0) > 100 || well.status === 'GUSHER' ? 1 : 0);
  const ransom = Math.round(eraDollars(state, KIDNAP.ransomBase * vis, KIDNAP.baseYear));
  removeFromAssignments(state, c);
  c.captive = id;
  c.injuredUntil = state.day + 100000; // held: off the roster, families get half pay
  const k = { id, crewId: c.id, name: c.name, wellId: well.id, fieldId: well.fieldId, regionId: rid, day: state.day, ransom, status: 'pending', deadline: state.day + 3, untilDay: null };
  P.kidnappings[id] = k;
  state.company.stats.kidnappings = (state.company.stats.kidnappings ?? 0) + 1;
  regionState(state, rid).incidents.push({ day: state.day, type: 'kidnapping', siteId: well.id });
  const text = `${c.name} (${roleName(c.role)}) was carried off from ${well.name} at ${fieldDef(well.fieldId)?.name}. The kidnappers want ${fmt$(ransom)}.`;
  queueDecision(state, {
    kind: 'kidnapping', id, kidnapId: id, day: state.day, title: 'Kidnapping', text,
    options: [
      { choice: 'ransom', label: `Pay the ransom (${fmt$(ransom)})`, desc: 'He comes home tomorrow. Paying encourages the next gang.', cost: ransom, odds: 1, disabled: state.company.cash < ransom },
      { choice: 'negotiate', label: 'Negotiate', desc: `${KIDNAP.negotiateDays[0]}–${KIDNAP.negotiateDays[1]} days of talks; each week ${Math.round(KIDNAP.releasePerWeek * 100)}% chance he is freed, ${Math.round(KIDNAP.deathPerWeek * 100)}% he is killed.`, odds: null },
      { choice: 'ignore', label: 'Refuse to deal', desc: `Reputation ${KIDNAP.ignoreRep}; ${Math.round(KIDNAP.ignoreDeath * 100)}% he does not come back.`, odds: 1 - KIDNAP.ignoreDeath },
    ],
  });
  notify(state, 'kidnapping', { crewId: c.id, wellId: well.id, fieldId: well.fieldId, regionId: rid, kidnapId: id, ransom, pause: true, level: 'warn', text });
  return k;
}

export function resolveKidnappingImpl(state, id, choice, auto = false) {
  const P = politicsState(state);
  const k = P.kidnappings[id];
  if (!k || k.status !== 'pending') return { ok: false, msg: 'No such kidnapping pending.' };
  if (!['ransom', 'negotiate', 'ignore'].includes(choice)) return { ok: false, msg: 'Choose ransom, negotiate or ignore.' };
  const c = state.crew[k.crewId];
  if (choice === 'ransom' && state.company.cash < k.ransom) return auto ? resolveKidnappingImpl(state, id, 'negotiate', true) : { ok: false, msg: `The ransom is ${fmt$(k.ransom)}.` };
  popDecision(state, id);
  if (choice === 'ransom') {
    spend(state, k.ransom, 'protection', false);
    const reg = regionState(state, k.regionId);
    (reg.tempMods ??= []).push({ delta: -0.02, untilDay: state.day + 365, why: 'ransom' });
    release(state, k, 'ransomed');
    return { ok: true, msg: `Paid ${fmt$(k.ransom)}. ${c?.name ?? 'He'} is home.` };
  }
  if (choice === 'negotiate') {
    k.status = 'negotiating';
    k.untilDay = state.day + int(state, KIDNAP.negotiateDays[0], KIDNAP.negotiateDays[1]);
    k.nextWeek = state.day + 7;
    return { ok: true, msg: `Talks begin through a local go-between for ${c?.name ?? 'your man'}.` };
  }
  state.company.reputation = clamp(state.company.reputation + KIDNAP.ignoreRep, 0, 100);
  if (chance(state, KIDNAP.ignoreDeath)) { kill(state, k); return { ok: true, msg: `You refused to deal. ${c?.name ?? 'He'} was killed.` }; }
  k.status = 'holding'; k.untilDay = state.day + int(state, 30, 90);
  return { ok: true, msg: `You refused to deal. ${c?.name ?? 'He'} is still held.` };
}

function release(state, k, how) {
  const c = state.crew[k.crewId];
  k.status = 'released'; k.outcome = how; k.endDay = state.day;
  if (c && c.status === 'active') { c.injuredUntil = null; c.captive = null; c.morale = Math.max(0, (c.morale ?? 60) - 20); }
  notify(state, 'news', { kind: 'security', regionId: k.regionId, fieldId: k.fieldId, text: `${k.name} has been released${how === 'ransomed' ? '' : ' after weeks in captivity'}.` });
}

function kill(state, k) {
  const c = state.crew[k.crewId];
  k.status = 'dead'; k.endDay = state.day;
  if (c && c.status === 'active') { c.injuredUntil = null; c.captive = null; injureCrew(state, c, 'was killed by his kidnappers', { fatalChance: 1, cause: 'kidnapping', wellId: k.wellId }); }
}

function tickKidnappings(state) {
  const P = state.politics;
  if (!P) return;
  for (const k of Object.values(P.kidnappings)) {
    if (k.status === 'pending' && state.day >= k.deadline) resolveKidnappingImpl(state, k.id, 'negotiate', true);
    else if (k.status === 'negotiating') {
      if (state.day >= k.untilDay) release(state, k, 'negotiated');
      else if (state.day >= k.nextWeek) {
        k.nextWeek = state.day + 7;
        if (chance(state, KIDNAP.releasePerWeek)) release(state, k, 'negotiated');
        else if (chance(state, KIDNAP.deathPerWeek)) kill(state, k);
      }
    } else if (k.status === 'holding' && state.day >= k.untilDay) release(state, k, 'escaped');
    if (['released', 'dead'].includes(k.status) && state.day - (k.endDay ?? state.day) > 730) delete P.kidnappings[k.id];
  }
}

export function startLaborStrike(state, well) {
  const rid = regionOfField(well.fieldId);
  const reg = regionState(state, rid);
  if (reg.strike && reg.strike.untilDay > state.day) return null;
  if ((reg.wagePremium ?? 0) >= LABOR_STRIKE.wageRaise - 1e-9 && chance(state, 0.7)) return null; // a raise keeps most crews content
  const until = state.day + LABOR_STRIKE.days;
  reg.strike = { startDay: state.day, untilDay: until, raised: false };
  for (const w of regionWells(state, rid)) if (w.prod) w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, until);
  reg.incidents.push({ day: state.day, type: 'labor_strike', siteId: well.id });
  const id = `strike_${rid}_${state.day}`;
  const cost = monthlyWageBill(state, rid) * LABOR_STRIKE.wageRaise;
  const text = `The oil workers walk out across ${regionDef(rid)?.name}. Not a barrel moves until wages go up ${Math.round(LABOR_STRIKE.wageRaise * 100)}%.`;
  queueDecision(state, {
    kind: 'laborStrike', id, regionId: rid, day: state.day, title: 'Labor strike', text,
    options: [
      { choice: 'raise', label: `Raise wages ${Math.round(LABOR_STRIKE.wageRaise * 100)}%`, desc: `About ${fmt$(cost)} a month more for your crews in the region, for good. Production resumes tomorrow.`, cost },
      { choice: 'wait', label: 'Wait them out', desc: `No production in the region for ${LABOR_STRIKE.days} days.` },
    ],
  });
  notify(state, 'laborStrike', { regionId: rid, fieldId: well.fieldId, untilDay: until, pause: true, level: 'warn', text });
  return reg.strike;
}

export function resolveLaborStrikeImpl(state, id, choice) {
  const d = [state.pendingDecision, ...(state.decisionQueue ?? [])].find((x) => x?.id === id);
  if (!d) return { ok: false, msg: 'No such strike.' };
  if (!['raise', 'wait'].includes(choice)) return { ok: false, msg: 'Choose raise or wait.' };
  popDecision(state, id);
  const reg = regionState(state, d.regionId);
  if (choice === 'raise') {
    reg.wagePremium = LABOR_STRIKE.wageRaise;
    const strikeUntil = reg.strike?.untilDay ?? state.day;
    if (reg.strike) { reg.strike.untilDay = state.day; reg.strike.raised = true; }
    // lift only the strike's shut-in (sabotage/bust shut-ins that run longer stay)
    for (const w of regionWells(state, d.regionId)) if (w.prod && w.prod.forcedUntil > state.day && w.prod.forcedUntil <= strikeUntil) w.prod.forcedUntil = state.day;
    return { ok: true, msg: 'Wages raised; the men are back at the wells.' };
  }
  return { ok: true, msg: 'You wait the strike out.' };
}

function regionWells(state, rid) {
  const fids = new Set(fieldsInRegion(state, rid));
  return Object.values(state.wells).filter((w) => fids.has(w.fieldId) && !GONE.has(w.status));
}

function regionCrew(state, rid) {
  return Object.values(state.crew).filter((c) => c.status === 'active' && c.rigId && regionOfField(state.rigs[c.rigId]?.at?.fieldId) === rid);
}
function monthlyWageBill(state, rid) { return regionCrew(state, rid).reduce((a, c) => a + crewWage(state, c), 0) * 30; }

// ---- scripted region events (news effect type 'regionEvent') ------------------------------------
export function applyRegionEvent(state, item) {
  const e = item.effect;
  if (e?.type !== 'regionEvent' || !m2On(state)) return;
  const rid = e.regionId;
  if (!regionDef(rid)) return;
  const reg = regionState(state, rid);
  const s = e.severity ?? 0.5;
  const regionSites = sites(state).filter((w) => regionOfField(w.fieldId) === rid && w.status !== 'SEIZED');
  switch (e.event) {
    case 'sabotage': {
      let n = 0;
      for (const w of regionSites) {
        const rig = w.rigId ? state.rigs[w.rigId] : null;
        if (rig) rig.condition = Math.max(0, rig.condition - s * 100);
        if (w.prod) w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, state.day + Math.round(30 * s));
        w.attacked = { day: state.day, type: 'sabotage', damaged: true, fire: s >= 0.5 };
        if (s >= 0.5) w.fire = { day: state.day, untilDay: state.day + 5 };
        n++;
      }
      for (const fid of fieldsInRegion(state, rid)) {
        const st = state.fields[fid].storage;
        if (st.bbl > 0) { st.bbl *= 1 - s * 0.5; st.barrels = Math.floor(st.barrels * (1 - s * 0.5)); n++; }
      }
      if (n) notify(state, 'news', { kind: 'security', regionId: rid, level: 'warn', text: `Your property in ${regionDef(rid).name} was wrecked: rigs −${Math.round(s * 100)}%, wells down ${Math.round(30 * s)} days, ${Math.round(s * 50)}% of stored oil lost.` });
      break;
    }
    case 'attack':
      for (const w of regionSites) if (chance(state, s)) startIncident(state, w, 'rebel_attack');
      break;
    case 'occupation':
      reg.occupiedUntil = Math.max(reg.occupiedUntil ?? 0, state.day + (e.days ?? 60));
      break;
    case 'threat':
      reg.threatUntil = Math.max(reg.threatUntil ?? 0, state.day + 365);
      if (regionSites.length || hasAssets(state, rid)) {
        notify(state, 'news', { kind: 'politics', regionId: rid, level: 'warn', text: `Concession in doubt in ${regionDef(rid).name}: insurers double their premiums.` });
        offerPartners(state, rid, true);
      }
      break;
    case 'expropriate':
      seizeRegion(state, rid, e.compensation ?? null, item.headline ?? 'Expropriation');
      break;
    default:
      break;
  }
}

function offerPartners(state, rid, force = false) {
  const reg = regionState(state, rid);
  if (reg.partner) return;
  reg.offered ??= {};
  for (const p of partnersOnOffer(rid, yf(state))) {
    if (reg.offered[p.id] && !force) continue;
    reg.offered[p.id] = state.day;
    queueDecision(state, {
      kind: 'partnerOffer', id: `partner_${rid}_${p.id}_${state.day}`, regionId: rid, partnerId: p.id, day: state.day, title: `A local partner: ${p.name}`,
      text: p.desc,
      options: [
        { choice: 'accept', label: `Accept (${Math.round(p.revenueShare * 100)}% of revenue)`, desc: `Attack risk ×${p.attackMult}${p.expropriationMult !== 1 ? `, expropriation risk ×${p.expropriationMult}` : ''}.` },
        { choice: 'decline', label: 'Decline' },
      ],
    });
    return;
  }
}

export function resolvePartnerOfferImpl(state, id, choice) {
  const d = [state.pendingDecision, ...(state.decisionQueue ?? [])].find((x) => x?.id === id);
  if (!d) return { ok: false, msg: 'No such offer.' };
  if (!['accept', 'decline'].includes(choice)) return { ok: false, msg: 'Choose accept or decline.' };
  popDecision(state, id);
  if (choice === 'accept') return setPartnerImpl(state, d.regionId, d.partnerId);
  return { ok: true, msg: 'You go it alone.' };
}

// ---- daily / monthly ticks -------------------------------------------------------------------
function presentRegions(state) {
  const out = new Set();
  for (const w of allWells(state)) if (!GONE.has(w.status) && !w.ownerOperatorId) { const r = regionOfField(w.fieldId); if (r) out.add(r); }
  for (const r of Object.values(state.rigs)) if (r.at?.fieldId) { const rid = regionOfField(r.at.fieldId); if (rid) out.add(rid); }
  for (const f of Object.values(state.fields)) {
    if (f.storage?.bbl > 1 || f.plots.some((p) => p.owned)) { const rid = f.regionId ?? regionOfField(f.id); if (rid) out.add(rid); }
  }
  // perf: items are many and mostly not on fields; one region lookup per distinct field (same Set insertion order)
  let last = null;
  for (const k in state.items) { const l = state.items[k].loc; if (l.type === 'field' && l.id !== last) { last = l.id; const rid = regionOfField(l.id); if (rid) out.add(rid); } }
  return out;
}

export function tickPolitics(state) {
  if (!m2On(state)) return;
  const day = state.day;
  const present = presentRegions(state);
  for (const rid of present) {
    if (!regionDef(rid)) continue;
    const reg = regionState(state, rid);
    // closures seize whatever is still there on (or after) the closure date
    const c = closureNow(state, rid);
    if (c) {
      const key = `${c.from}`;
      if (reg.closureSeized !== key) { reg.closureSeized = key; seizeRegion(state, rid, c.compensation, c.reason, { compensationDays: NATIONALIZATIONS.find((n) => n.id === c.id)?.compensationDays }); }
      continue;
    }
    // annual expropriation hazard, rolled daily
    const h = expropriationHazard(state, rid);
    if (h.daily > 0 && chance(state, h.daily)) { seizeRegion(state, rid, null, `The government of ${regionDef(rid).country ?? regionDef(rid).name} nationalizes foreign oil property`); continue; }
    // partner windows: drop expired partners, offer new ones once
    if (reg.partner && !activePartner(state, rid)) {
      const old = partnerDef(rid, reg.partner);
      reg.partner = null;
      notify(state, 'news', { kind: 'security', regionId: rid, text: `${old?.name ?? 'Your local partner'} is gone; your camps in ${regionDef(rid).name} are on their own again.` });
    }
    if (!reg.partner && partnersOnOffer(rid, yf(state)).some((p) => !reg.offered?.[p.id]) && sites(state).some((w) => regionOfField(w.fieldId) === rid)) offerPartners(state, rid);
    // strike wage premium
    if (reg.wagePremium > 0) {
      const extra = regionCrew(state, rid).reduce((a, cr) => a + crewWage(state, cr), 0) * reg.wagePremium;
      if (extra > 0) spend(state, extra, 'wages', false);
    }
  }
  for (const reg of Object.values(state.regions ?? {})) {
    if (reg.tempMods?.length) reg.tempMods = reg.tempMods.filter((m) => m.untilDay > day);
    if (reg.strike && reg.strike.untilDay <= day) reg.strike = null;
  }
  tickKidnappings(state);
  const P = state.politics;
  if (P) {
    // compensation arrives
    for (const comp of P.compensations) {
      if (!comp.paid && day >= comp.dueDay) {
        comp.paid = day;
        earn(state, comp.amount, 'recoveries');
        notify(state, 'news', { kind: 'finance', regionId: comp.regionId, text: `${regionDef(comp.regionId)?.name}: compensation of ${fmt$(comp.amount)} paid for your seized property.` });
      }
    }
    P.compensations = P.compensations.filter((cp) => !cp.paid || day - cp.paid < 365);
    // insurance renewals (annual premium, re-quoted)
    for (const [rid, pol] of Object.entries(P.policies)) {
      if (day >= pol.untilDay) { delete P.policies[rid]; if (state.regions?.[rid]) state.regions[rid].insurance = null; continue; }
      if (day >= pol.paidUntilDay) {
        const prem = pol.insuredValue * insuranceRate(state, rid);
        if (state.company.cash >= prem) { spend(state, prem, 'insurance'); pol.premiumPerYear = prem; pol.paidUntilDay = day + 365; }
        else { delete P.policies[rid]; if (state.regions?.[rid]) state.regions[rid].insurance = null; notify(state, 'news', { kind: 'finance', regionId: rid, level: 'warn', text: `Your political-risk policy in ${regionDef(rid)?.name} lapsed: premium unpaid.` }); }
      }
    }
  }
  if (dayToDate(day).dom === 1) tickBribesMonthly(state);
}

function tickBribesMonthly(state) {
  for (const [rid, reg] of Object.entries(state.regions ?? {})) {
    if (!(reg.bribe > 0)) continue;
    if (!spend(state, reg.bribe, 'protection')) { reg.bribe = 0; notify(state, 'news', { kind: 'security', regionId: rid, level: 'warn', text: `No cash for the envelopes in ${regionDef(rid)?.name}; the bribes stop.` }); continue; }
    const corr = regionRisk(rid, yf(state)).corruption;
    if (corr > BRIBE_SCANDAL.corruptionAbove && chance(state, BRIBE_SCANDAL.perMonth)) {
      state.company.reputation = clamp(state.company.reputation + BRIBE_SCANDAL.rep, 0, 100);
      notify(state, 'news', { kind: 'politics', regionId: rid, level: 'warn', text: `Scandal! Newspapers print your payments to officials in ${regionDef(rid)?.name}. Reputation ${BRIBE_SCANDAL.rep}.` });
    }
  }
}

// ---- view (merged into security.regionView) -----------------------------------------------------
export function regionM2View(state, rid) {
  const reg = state.regions?.[rid] ?? {};
  const y = yf(state);
  const closure = regionClosure(rid, y);
  const fid = fieldsInRegion(state, rid)[0] ?? null;
  return {
    closure: closure ? { ...closure } : null,
    blocked: fid ? regionBlock(state, fid) : null,
    occupied: reg.occupiedUntil > state.day ? { untilDay: reg.occupiedUntil } : null,
    threat: reg.threatUntil > state.day ? { untilDay: reg.threatUntil } : null,
    strike: reg.strike && reg.strike.untilDay > state.day ? { ...reg.strike } : null,
    wagePremium: reg.wagePremium ?? 0,
    tempMods: (reg.tempMods ?? []).filter((m) => m.untilDay > state.day),
    partner: { current: activePartner(state, rid), available: partnersOnOffer(rid, y) },
    bribe: { perMonth: reg.bribe ?? 0, needed: bribeNeeded(state, rid), effect: bribeEffect(state, rid), scandalChance: regionRisk(rid, y).corruption > BRIBE_SCANDAL.corruptionAbove && reg.bribe > 0 ? BRIBE_SCANDAL.perMonth : 0 },
    attackMult: attackMult(state, rid),
    insurance: { policy: state.politics?.policies?.[rid] ?? null, quote: insuranceQuote(state, rid), lloyds: state.tokens?.insurance?.[rid] ?? null },
    expropriation: expropriationHazard(state, rid),
    bookValue: bookValue(state, rid),
    royalty: scheduledRoyalty(rid, y),
    severance: severanceRate(rid, y),
    localPriceMult: fid ? localPriceMult(state, fid) : 1,
    seized: reg.seized ?? null,
    seizures: (state.politics?.seizures ?? []).filter((s) => s.regionId === rid),
    compensationsDue: (state.politics?.compensations ?? []).filter((c) => c.regionId === rid && !c.paid),
    kidnappings: Object.values(state.politics?.kidnappings ?? {}).filter((k) => k.regionId === rid && !['released', 'dead'].includes(k.status)),
  };
}
